import { useQuery } from "@tanstack/react-query";
import { clientesApi } from "./axios";
import {
  DataI,
  MigrationsI,
  PropsI,
  ServiceResponseI,
  ClientI,
  ObjPostI,
  OneClientI,
  ClientPropsI,
} from "../../interfaces";
import { ValidateCurrentToken, LogoutSystem } from "../../helpers";
import { AxiosError } from "axios";

const getAllClients = async ({
  pagination,
  sorting,
  globalFilter,
  status,
}: ClientPropsI): Promise<ServiceResponseI<DataI<ClientI>>> => {
  try {
    const validateToken = ValidateCurrentToken();

    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();

    params.append("page", (pagination!.pageIndex + 1).toString());
    params.append("limit", pagination!.pageSize.toString());
    globalFilter && params.append("search", globalFilter);
    sorting && params.append("sorting", JSON.stringify(sorting));
    status && params.append("status", status);
    
    //headers: { 'Authorization': validateToken
    const { data } = await clientesApi.get<ServiceResponseI<DataI<ClientI>>>(
      "",
      { params, headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const getAllClientsNoPagination = async (): Promise<
  ServiceResponseI<OneClientI[]>
> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await clientesApi.get<ServiceResponseI<OneClientI[]>>(
      `/all-no-pagination`,
      { headers: { Authorization: validateToken } },
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const getClientes = async (urlConcat: string | null) => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    if (urlConcat) {
      const { data } = await clientesApi.get(urlConcat);
      return data;
    }

    const { data } = await clientesApi.get("");
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const useClients = ({
  sorting,
  pagination,
  globalFilter,
  status
}: ClientPropsI) => {
  const clientesQuery = useQuery(
    ["clientes-all", { pagination, sorting, globalFilter, status }],
    () => getAllClients({ pagination, sorting, globalFilter, status }),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  );

  return {
    clientesQuery,
  };
};


export const clientsPost = async <T>(
  getForms: T,
): Promise<ServiceResponseI<ObjPostI>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await clientesApi.post<ServiceResponseI<ObjPostI>>(
      "",
      getForms,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const clientsPut = async <T>(
  id: string,
  updateForms: T,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await clientesApi.put<ServiceResponseI<string>>(
      `/${id}`,
      updateForms,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const clientById = async (
  id: string,
): Promise<ServiceResponseI<ClientI>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await clientesApi.get<ServiceResponseI<ClientI>>(
      `/${id}`,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const clientDelete = async (
  id: string,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await clientesApi.delete<ServiceResponseI<string>>(
      `/${id}`,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const clientSetStatus = async <T>(
  id: string,
  payload: T,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await clientesApi.patch<ServiceResponseI<string>>(
      `/set-status/${id}`,
      payload,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};
